import { useForkRef } from "@mui/material";
import { PopperProps } from "@mui/material/Popper";
import React, {
  Children,
  cloneElement,
  isValidElement,
  PropsWithRef,
  ReactElement,
  ReactNode,
  Ref,
  useState,
} from "react";
import { ARROW_SIZE } from "./styledComponents";
import { useHoverIntent } from "./useHoverIntent";

export interface UseHoverCardOptions {
  /**
   * Existing DOM element to anchor the card to (e.g. the web-component host that captured
   * the trigger markup). Takes precedence over `children`; when omitted, the first child
   * element is used as the anchor instead.
   */
  anchorElement?: HTMLElement;
  /**
   * Trigger rendered inline; the card anchors to the first child element. That child must
   * forward its `ref` to a DOM node. Ignored when `anchorElement` is provided.
   */
  children?: ReactNode;
}

export interface HoverCard {
  /** The cloned trigger element to render, or `null` when anchoring to `anchorElement`. */
  trigger: ReactElement | null;
  /** True once the card has opened at least once - use it to gate a lazy data fetch. */
  hasOpened: boolean;
  /** Spread onto the popper. */
  popperProps: Pick<PopperProps, "anchorEl" | "modifiers" | "open" | "placement">;
  /** Spread onto the card surface so moving the pointer onto it keeps the card open. */
  paperProps: Pick<React.ComponentProps<"div">, "onMouseEnter" | "onMouseLeave">;
  /** Attach to the arrow element so popper.js can position it. */
  arrowRef: (element: HTMLElement | null) => void;
}

/** First valid React element among `children`, or `null` when there is none. */
const getFirstElement = (children: ReactNode): ReactElement | null =>
  Children.toArray(children).find(isValidElement) ?? null;

/** Read a child's forwarded ref. React 19 moved `ref` into `props`; `element.ref` is deprecated. */
const getElementRef = (element: ReactElement | null): Ref<HTMLElement> | null =>
  element === null ? null : ((element.props as { ref?: Ref<HTMLElement> }).ref ?? null);

/**
 * Wires up an interactive hover card: resolves the anchor (either an explicit `anchorElement`,
 * e.g. a web-component host, or the first child element passed in), runs the hover-intent state
 * machine and returns everything a popper needs. The popper flips above the trigger automatically
 * when there isn't room below - popper.js measures the real rendered size, so no height estimate
 * is needed.
 *
 * It is a hook rather than a wrapper component so the caller can keep its own data fetching and
 * gate it on `hasOpened`.
 */
export const useHoverCard = ({ anchorElement, children }: UseHoverCardOptions): HoverCard => {
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
  // DOM node captured from the first child when no explicit `anchorElement` is given.
  const [childAnchor, setChildAnchor] = useState<HTMLElement | null>(null);

  // Render the child trigger (and capture its node) only when not given an explicit anchor.
  const firstChild = anchorElement === undefined ? getFirstElement(children) : null;
  const childRef = useForkRef<HTMLElement>(getElementRef(firstChild), setChildAnchor);
  const trigger = firstChild === null ? null : cloneElement<PropsWithRef<any>>(firstChild, { ref: childRef });

  const resolvedAnchor = anchorElement ?? childAnchor;
  const { open, hasOpened, openNow, scheduleClose } = useHoverIntent({
    anchorElement: resolvedAnchor,
    forceInlineBlock: anchorElement !== undefined,
  });

  return {
    trigger,
    hasOpened,
    popperProps: {
      anchorEl: resolvedAnchor,
      modifiers: [
        { name: "offset", options: { offset: [0, ARROW_SIZE] } },
        { name: "flip", options: { fallbackPlacements: ["top"] } },
        { name: "preventOverflow", options: { padding: 8 } },
        { name: "arrow", options: { element: arrowEl, padding: 8 } },
      ],
      open,
      placement: "bottom",
    },
    paperProps: { onMouseEnter: openNow, onMouseLeave: scheduleClose },
    arrowRef: setArrowEl,
  };
};
